<template>
  <div class="engagement-quality-section" v-loading="isLoading">
    <h3 class="section-title">Engagement Quality</h3>
    
    <div class="quality-card">
      <!-- Overall Engagement Section -->
      <div class="overall-engagement">
        <ul class="section-bullet-title">
          <li>Overall Engagement</li>
        </ul>
        <p class="section-description">
          Summarizes overall engagement quality based on chat responsiveness and sentiment signals.
        </p>
        <div class="engagement-level-box" :class="qualityLevel">
          <svg v-if="qualityLevel === 'healthy'" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M31 22C32.6569 22 34 20.6569 34 19C34 17.3431 32.6569 16 31 16C29.3431 16 28 17.3431 28 19C28 20.6569 29.3431 22 31 22Z" fill="#25B8C2"/>
            <path d="M17 22C18.6569 22 20 20.6569 20 19C20 17.3431 18.6569 16 17 16C15.3431 16 14 17.3431 14 19C14 20.6569 15.3431 22 17 22Z" fill="#25B8C2"/>
            <path d="M24 36C28.56 36 32.44 32.68 34 28H14C15.56 32.68 19.44 36 24 36Z" fill="#25B8C2"/>
            <path d="M23.98 4C12.94 4 4 12.96 4 24C4 35.04 12.94 44 23.98 44C35.04 44 44 35.04 44 24C44 12.96 35.04 4 23.98 4ZM24 40C15.16 40 8 32.84 8 24C8 15.16 15.16 8 24 8C32.84 8 40 15.16 40 24C40 32.84 32.84 40 24 40Z" fill="#25B8C2"/>
          </svg>
          <svg v-else-if="qualityLevel === 'neutral'" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M18 28H30V31H18V28Z" fill="#FEB52E"/>
            <path d="M31 22C32.6569 22 34 20.6569 34 19C34 17.3431 32.6569 16 31 16C29.3431 16 28 17.3431 28 19C28 20.6569 29.3431 22 31 22Z" fill="#FEB52E"/>
            <path d="M17 22C18.6569 22 20 20.6569 20 19C20 17.3431 18.6569 16 17 16C15.3431 16 14 17.3431 14 19C14 20.6569 15.3431 22 17 22Z" fill="#FEB52E"/>
            <path d="M23.98 4C12.94 4 4 12.96 4 24C4 35.04 12.94 44 23.98 44C35.04 44 44 35.04 44 24C44 12.96 35.04 4 23.98 4ZM24 40C15.16 40 8 32.84 8 24C8 15.16 15.16 8 24 8C32.84 8 40 15.16 40 24C40 32.84 32.84 40 24 40Z" fill="#FEB52E"/>
          </svg>
          <svg v-else-if="qualityLevel === 'at_risk'" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M31 22C32.6569 22 34 20.6569 34 19C34 17.3431 32.6569 16 31 16C29.3431 16 28 17.3431 28 19C28 20.6569 29.3431 22 31 22Z" fill="#D10000"/>
            <path d="M17 22C18.6569 22 20 20.6569 20 19C20 17.3431 18.6569 16 17 16C15.3431 16 14 17.3431 14 19C14 20.6569 15.3431 22 17 22Z" fill="#D10000"/>
            <path d="M24 28C19.34 28 15.36 30.9 13.76 35H17.1C18.48 32.62 21.04 31 24 31C26.96 31 29.5 32.62 30.9 35H34.24C32.64 30.9 28.66 28 24 28ZM23.98 4C12.94 4 4 12.96 4 24C4 35.04 12.94 44 23.98 44C35.04 44 44 35.04 44 24C44 12.96 35.04 4 23.98 4ZM24 40C15.16 40 8 32.84 8 24C8 15.16 15.16 8 24 8C32.84 8 40 15.16 40 24C40 32.84 32.84 40 24 40Z" fill="#D10000"/>
          </svg>
          <p class="engagement-text">{{ engagementMessage }}</p>
        </div>
      </div>

      <!-- Divider -->
      <div class="divider"></div>

      <!-- Sentiment Strategy Section -->
      <div class="sentiment-strategy">
        <ul class="section-bullet-title">
          <li>Sentiment Strategy</li>
        </ul>
        <p class="section-description">
          {{ sentimentStrategyText }}
        </p>
        
        <!-- Risk Alert Box (shown when there are alerts) -->
        <div v-if="hasAlerts" class="risk-alert-box">
          <div class="alert-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <div class="alert-content">
            <p class="alert-title">Risk Alert</p>
            <p class="alert-message">{{ alertMessage }}</p>
          </div>
        </div>
        
        <!-- View Alert Messages Button -->
        <ResponsiveButton 
          display_name="View Alert Messages"
          button_type="outlined"
          built_in_theme="dark"
          width_type="fill-whole"
          size="medium"
          @click-button="showAlertsModal = true"
        >
          <template #prefix>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z" fill="#374957"/>
            </svg>
          </template>
        </ResponsiveButton>
        <!-- <Button
          color-type="dark-blue-stroke"
          style-text="width: 100%;"
          @click="showAlertsModal = true"
        >
          <div style="display: flex; align-items: center; gap: 8px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z" fill="#374957"/>
            </svg>
            <span>View Alert Messages</span>
          </div>
        </Button> -->
      </div>
    </div>

    <ActiveAlertsModal v-model="showAlertsModal" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import ActiveAlertsModal from './ActiveAlertsModal.vue';
import Button from '@/NewLibrary/Button.vue';

const store = useStore();
const showAlertsModal = ref(false);

const isLoading = computed(() => store.state.myPractice.clinicDashboard.patientEngagementQualityLoading);
const qualityData = computed(() => store.state.myPractice.clinicDashboard.patientEngagementQualitySummary);
const alertCount = computed(() => store.state.myPractice.clinicDashboard.activeAlertsData?.length || 0);

const qualityLevel = computed(() => qualityData.value?.engagement_quality?.level || 'healthy');

const engagementMessage = computed(() => {
  const level = qualityLevel.value;
  switch (level) {
    case 'healthy':
      return 'Your overall engagement level is: Healthy!';
    case 'neutral':
      return 'Your overall engagement level is: Neutral!';
    case 'at_risk':
      return 'Your overall engagement level is: Negative!';
    default:
      return 'Your overall engagement level is: Healthy!';
  }
});

const hasAlerts = computed(() => qualityData.value?.engagement_quality?.components?.frustration_escalation > 0);

const sentimentStrategyText = computed(() => {
  return qualityData.value?.sentiment_strategy || '';
});

const alertMessage = computed(() => {
  return 'Chat signals: Frustration detected in Practice View patients.';
});
</script>

<style scoped>
.engagement-quality-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #333;
  margin: 0;
}

.quality-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 15px 0 rgba(164, 164, 164, 0.25);
  padding: 16px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 480px;
  flex: 1;
}

.overall-engagement,
.sentiment-strategy {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.overall-engagement {
  padding-bottom: 4px;
}

.sentiment-strategy {
  padding-bottom: 8px;
}

.section-bullet-title {
  list-style-type: disc;
  padding-left: 24px;
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
}

.section-bullet-title li {
  padding-left: 0;
}

.section-description {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  margin: 0;
}

.engagement-level-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  background: #F2F2F2;
  border-radius: 5px;
}

.engagement-level-box.healthy .emoji-icon {
  color: #25B8C2;
}

.engagement-level-box.neutral .emoji-icon {
  color: #FEB52E;
}

.engagement-level-box.at_risk .emoji-icon,
.engagement-level-box.unhappy .emoji-icon {
  color: #D10000;
}

.emoji-icon {
  flex-shrink: 0;
}

.engagement-text {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.15px;
  text-align: center;
  margin: 0;
}

.engagement-level-box.healthy .engagement-text {
  color: #25B8C2;
}

.engagement-level-box.neutral .engagement-text {
  color: #FEB52E;
}

.engagement-level-box.at_risk .engagement-text,
.engagement-level-box.unhappy .engagement-text {
  color: #D10000;
}

.divider {
  height: 1px;
  background: #E0E0E0;
  width: 100%;
}

.risk-alert-box {
  display: flex;
  gap: 10px;
  padding: 10px 16px;
  background: #FAEAEA;
  border-radius: 8px;
  min-height: 80px;
  align-items: flex-start;
}

.alert-icon {
  flex-shrink: 0;
}

.alert-icon svg {
  color: #D10000;
}

.alert-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
}

.alert-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: #D10000;
  margin: 0;
}

.alert-message {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #D10000;
  margin: 0;
}

</style>
